import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './apiClient';

/**
 * Retry only genuinely transient failures — offline (status 0), timeout (408), rate-limit (429) and
 * server errors (5xx). Client errors (validation, auth, forbidden, not-found, conflict, …) are never
 * retried because a retry can't change the outcome; retrying them just delays the friendly message.
 */
function isTransient(error: unknown): boolean {
  const status = error instanceof ApiError ? error.status : 0;
  return status === 0 || status === 408 || status === 429 || status >= 500;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => isTransient(error) && failureCount < 2,
      // Exponential backoff, capped, so a flaky network/server recovers without hammering it.
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true, // auto-refetch stale data when the connection returns (offline → online)
    },
    mutations: {
      // Mutations are not idempotent — never auto-retry; the user re-submits on a friendly error.
      retry: false,
    },
  },
});
